import { useState } from 'react';
import {
  Tooltip,
  Popover,
  Box,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  RadioGroup,
  FormControlLabel,
  Radio,
  Checkbox,
  Switch,
  FormLabel,
} from '@mui/material';
import PropTypes from 'prop-types';
import Iconify from 'src/components/Iconify';
import { formatDateToEnd, formatDateToStart, formatDateShortLocal } from 'src/utils/formatTime';
import Button from 'src/components/Button';

const FilterTooltip = ({
  filters,
  onFilterChange,
  filterOptions,
  iconColor = '#ffffff',
  buttonLabel = 'Filter',
}) => {
  const [anchorEl, setAnchorEl] = useState(null);

  const handleClick = event => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);

  const handleFilterChange = (key, value, closeOnChange = false) => {
    onFilterChange({ ...filters, [key]: value });
    if (closeOnChange) {
      handleClose();
    }
  };

  const isOptionDisabled = option =>
    typeof option.disabled === 'function' ? option.disabled(filters) : !!option.disabled;

  return (
    <>
      <Tooltip title="Filter">
        <Button
          variant="outlined"
          onClick={handleClick}
          startIcon={<Iconify icon="mdi:filter-variant" color={iconColor} />}
          styles={{
            backgroundColor: '#3F3F3F',
            borderColor: '#2E3236',
            color: '#ffffff',
            textTransform: 'none',
            padding: '0 10px',
            minHeight: 32,
            height: 32,
            lineHeight: 1,
          }}>
          {buttonLabel}
        </Button>
      </Tooltip>
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}>
        <Box sx={{ p: 1.25, minWidth: 188 }}>
          {filterOptions.map((option, index) => (
            <Box sx={{ mb: 1 }} key={index}>
              {option.type === 'select' && (
                <FormControl fullWidth size="small">
                  <InputLabel sx={{ fontSize: '0.8125rem' }}>{option.label}</InputLabel>
                  <Select
                    value={filters[option.key] ?? ''}
                    disabled={isOptionDisabled(option)}
                    onChange={e =>
                      handleFilterChange(option.key, e.target.value, option.closeOnChange)
                    }
                    label={option.label}
                    sx={{ fontSize: '0.8125rem' }}>
                    {option.options.map((opt, idx) => (
                      <MenuItem key={idx} value={opt.value} sx={{ fontSize: '0.8125rem', minHeight: 32 }}>
                        {opt.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}

              {option.type === 'number' && (
                <TextField
                  size="small"
                  type="number"
                  label={option.label}
                  value={filters[option.key] ?? ''}
                  onChange={e =>
                    handleFilterChange(option.key, e.target.value, option.closeOnChange)
                  }
                  InputLabelProps={{ shrink: true }}
                  sx={{ '& .MuiInputBase-root': { fontSize: '0.8125rem' } }}
                />
              )}

              {option.type === 'radio' && (
                <FormControl component="fieldset">
                  <FormLabel sx={{ fontSize: '0.8125rem' }}>{option.label}</FormLabel>
                  <RadioGroup
                    value={filters[option.key] || ''}
                    onChange={e =>
                      handleFilterChange(option.key, e.target.value, option.closeOnChange)
                    }>
                    {option.options.map((opt, idx) => (
                      <FormControlLabel
                        key={idx}
                        value={opt.value}
                        control={
                          <Radio
                            size="small"
                            onClick={e => {
                              if (filters[option.key] === opt.value) {
                                e.preventDefault();
                                handleFilterChange(option.key, '', option.closeOnChange);
                              }
                            }}
                          />
                        }
                        sx={{ '& .MuiFormControlLabel-label': { fontSize: '0.8125rem' } }}
                        label={opt.label}
                      />
                    ))}
                  </RadioGroup>
                </FormControl>
              )}

              {option.type === 'checkbox' && (
                <FormControl component="fieldset">
                  <FormLabel sx={{ fontSize: '0.8125rem' }}>{option.label}</FormLabel>
                  {option.options.map((opt, idx) => (
                    <Tooltip
                      key={idx}
                      title={
                        (typeof opt.tooltip === 'function' ? opt.tooltip(filters) : opt.tooltip) ||
                        ''
                      }
                      disableHoverListener={
                        !(typeof opt.disabled === 'function' ? opt.disabled(filters) : opt.disabled)
                      }
                      placement="right">
                      <span>
                        <FormControlLabel
                          control={
                            <Checkbox
                              size="small"
                              disabled={
                                typeof opt.disabled === 'function'
                                  ? opt.disabled(filters)
                                  : !!opt.disabled
                              }
                              checked={filters[option.key]?.includes(opt.value) || false}
                              onChange={e => {
                                const currentValues = filters[option.key] || [];
                                const newValues = e.target.checked
                                  ? [...currentValues, opt.value]
                                  : currentValues.filter(val => val !== opt.value);
                                handleFilterChange(option.key, newValues, option.closeOnChange);
                              }}
                            />
                          }
                          sx={{
                            '& .MuiFormControlLabel-label': { fontSize: '0.8125rem' },
                            my: -0.25,
                          }}
                          label={opt.label}
                        />
                      </span>
                    </Tooltip>
                  ))}
                </FormControl>
              )}

              {option.type === 'switch' && (
                <FormControlLabel
                  control={
                    <Switch
                      size="small"
                      checked={!!filters[option.key]}
                      onChange={e =>
                        handleFilterChange(option.key, e.target.checked, option.closeOnChange)
                      }
                    />
                  }
                  sx={{ '& .MuiFormControlLabel-label': { fontSize: '0.8125rem' } }}
                  label={option.label}
                />
              )}

              {option.type === 'date' && (
                <Box>
                  <TextField
                    size="small"
                    type="date"
                    label={option.label}
                    value={filters[option.key] ? formatDateShortLocal(filters[option.key]) : ''}
                    onChange={e => {
                      let date = null;
                      if (e.target.value) {
                        if (option.key === 'dateFrom') {
                          date = formatDateToStart(e.target.value);
                        } else if (option.key === 'dateTo') {
                          date = formatDateToEnd(e.target.value);
                        } else {
                          date = new Date(e.target.value);
                        }
                      }
                      handleFilterChange(option.key, date, option.closeOnChange);
                    }}
                    InputLabelProps={{
                      shrink: true,
                    }}
                    sx={{ mb: 1, '& .MuiInputBase-root': { fontSize: '0.8125rem' } }}
                  />
                </Box>
              )}
            </Box>
          ))}
        </Box>
      </Popover>
    </>
  );
};

FilterTooltip.propTypes = {
  filters: PropTypes.object.isRequired,
  onFilterChange: PropTypes.func.isRequired,
  iconColor: PropTypes.string,
  buttonLabel: PropTypes.string,
  filterOptions: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      type: PropTypes.oneOf(['select', 'number', 'radio', 'checkbox', 'switch', 'date']).isRequired,
      label: PropTypes.string.isRequired,
      closeOnChange: PropTypes.bool,
      disabled: PropTypes.oneOfType([PropTypes.bool, PropTypes.func]),
      options: PropTypes.arrayOf(
        PropTypes.shape({
          value: PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.bool])
            .isRequired,
          label: PropTypes.string.isRequired,
          disabled: PropTypes.oneOfType([PropTypes.bool, PropTypes.func]),
          tooltip: PropTypes.oneOfType([PropTypes.string, PropTypes.func]),
        }),
      ),
    }),
  ).isRequired,
};

export default FilterTooltip;
